import { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { appIntentService } from '@/services/app-intent-service'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/use-toast'
import {
  TASKBAR_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
  DEFAULT_WINDOW_HEIGHT,
  WINDOW_CASCADE_OFFSET_X,
  WINDOW_CASCADE_OFFSET_Y,
  calculateResponsiveWindowSize,
} from '@/shared/ui'
import type { WindowParams } from '@/types/window'
import { deepLinkService, resolveDeepLinkParamKey } from '@/services/deep-link-service'
import { windowIdentityKey } from '@/lib/window-identity'
import { guardedWindowIds, type CloseGuard, type CloseGuardVerdict } from '@/lib/close-guard'
import { APPS } from '@/data/apps'
import { logError } from '@/lib/logger'
import type { PersistedWindowState } from '@/types/settings'
import {
  serializeWindow,
  saveToLocalStorage,
  loadFromLocalStorage,
  saveToBackend,
  loadFromBackend,
  consumeSkipRestore,
} from '@/services/window-state-service'

type WindowIdentity = { appId: string; params?: unknown }

function windowKey(w: WindowIdentity): string {
  return w.appId + JSON.stringify(w.params ?? null)
}

function windowSignature(windows: WindowIdentity[]): string {
  return JSON.stringify(windows.map(windowKey))
}

function restoreWindow(w: PersistedWindowState, index: number, title: string): WindowItem {
  return {
    id: generateUUID(),
    appId: w.appId,
    title,
    subtitle: w.subtitle,
    minimized: w.minimized,
    createdAt: Date.now(),
    zIndex: 1000 + index,
    position: w.position,
    isCustomPosition: true,
    size: w.size,
    isMaximized: Boolean(w.isMaximized),
    snapSide: w.snapSide,
    params: w.params as WindowParams | undefined,
  }
}

// UUID Generator für eindeutige Fenster-IDs
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

type WindowPosition = {
  x: number
  y: number
}

type WindowSize = {
  w: number
  h: number
}

type WindowParamPrimitive = string | number | boolean

function isWindowParamPrimitive(value: unknown): value is WindowParamPrimitive {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
}

function normalizeWindowParams(params?: WindowParams): WindowParams | undefined {
  if (!params) return undefined

  const normalized = new Map<string, WindowParamPrimitive>()

  for (const [key, rawValue] of Object.entries(params)) {
    const resolvedKey = resolveDeepLinkParamKey(key)

    if (rawValue === undefined || rawValue === null) {
      continue
    }

    if (isWindowParamPrimitive(rawValue)) {
      normalized.set(resolvedKey, rawValue)
    }
  }

  return normalized.size > 0 ? Object.fromEntries(normalized) as WindowParams : undefined
}

function applyWindowParamsPatch(current: WindowParams | undefined, patch: WindowParams): WindowParams | undefined {
  const result = new Map<string, WindowParamPrimitive>()

  const normalizedCurrent = normalizeWindowParams(current)
  if (normalizedCurrent) {
    for (const [key, value] of Object.entries(normalizedCurrent)) {
      if (isWindowParamPrimitive(value)) {
        result.set(key, value)
      }
    }
  }

  for (const [key, rawValue] of Object.entries(patch)) {
    const resolvedKey = resolveDeepLinkParamKey(key)

    if (rawValue === undefined || rawValue === null) {
      result.delete(resolvedKey)
      continue
    }

    if (isWindowParamPrimitive(rawValue)) {
      result.set(resolvedKey, rawValue)
    }
  }

  return result.size > 0 ? Object.fromEntries(result) as WindowParams : undefined
}

function areWindowParamsEqual(a?: WindowParams, b?: WindowParams): boolean {
  if (!a && !b) return true
  if (!a || !b) return false

  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false

  return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && a[key] === b[key])
}

export type WindowItem = {
  id: string // Eindeutige UUID für diese Fenster-Instanz
  appId: string // App-Typ (z.B. 'drive', 'settings')
  title: string
  subtitle?: string // Optionaler Untertitel/Kontext (z.B. "Übersicht", "Ordner: Marketing")
  minimized: boolean
  createdAt: number
  zIndex: number // Z-Index für Stacking-Order (höher = weiter vorne)
  position: WindowPosition
  isCustomPosition: boolean
  size: WindowSize
  isMaximized: boolean
  snapSide: 'left' | 'right' | null
  params?: WindowParams // Deep-Link-Parameter für die App
}

export type WindowMinimizedState = {
  id: string
  minimized: boolean
}

const FALLBACK_POSITION: WindowPosition = { x: 80, y: 80 }

const DEFAULT_SIZE: WindowSize = { w: DEFAULT_WINDOW_WIDTH, h: DEFAULT_WINDOW_HEIGHT }

function computeCenteredPosition(size: WindowSize = DEFAULT_SIZE): WindowPosition {
  if (typeof window === 'undefined') {
    return { ...FALLBACK_POSITION }
  }

  const availableWidth = window.innerWidth
  const availableHeight = Math.max(0, window.innerHeight - TASKBAR_HEIGHT)
  const x = Math.max(0, Math.round((availableWidth - size.w) / 2))
  const y = Math.max(0, Math.round((availableHeight - size.h) / 2))

  return { x, y }
}

function computeCascadePosition(base: WindowPosition, size: WindowSize = DEFAULT_SIZE): WindowPosition {
  const nextX = base.x + WINDOW_CASCADE_OFFSET_X
  const nextY = base.y + WINDOW_CASCADE_OFFSET_Y

  if (typeof window === 'undefined') {
    return { x: nextX, y: nextY }
  }

  const maxX = Math.max(0, window.innerWidth - size.w)
  const maxY = Math.max(0, window.innerHeight - TASKBAR_HEIGHT - size.h)

  return {
    x: Math.min(nextX, maxX),
    y: Math.min(nextY, maxY),
  }
}

function computeInitialPosition(existing: WindowItem[], activeId: string | null, size: WindowSize = DEFAULT_SIZE): WindowPosition {
  if (existing.length === 0) {
    return computeCenteredPosition(size)
  }

  const cascadeCandidates = existing.filter((item) => !item.isCustomPosition)
  if (cascadeCandidates.length === 0) {
    return computeCenteredPosition(size)
  }

  const activeWindow = activeId
    ? cascadeCandidates.find((item) => item.id === activeId)
    : null
  const fallbackSource = cascadeCandidates.reduce<WindowItem>((latest, item) => {
    return item.createdAt > latest.createdAt ? item : latest
  }, cascadeCandidates[0])
  const basePosition = activeWindow?.position ?? fallbackSource.position ?? FALLBACK_POSITION

  return computeCascadePosition(basePosition, size)
}

/** Re-exported so an app can type its guard without a second import. */
export type { CloseGuard, CloseGuardVerdict }

/**
 * `skipGuard` schliesst ohne Rueckfrage (#850).
 *
 * Nur fuer MASCHINELLES Schliessen gedacht — der URL-Abgleich in `useDeepLink`
 * etwa. Dort ist eine Rueckfrage nicht erfuellbar: Die URL hat sich bereits
 * geaendert, und ein „offen lassen" liesse Fenster und Adresse dauerhaft
 * auseinanderlaufen. Der Guard gilt dem MENSCHEN, der auf Schliessen klickt.
 */
type CloseOptions = { skipGuard?: boolean }

/** Welche Fenster auf eine Bestaetigung warten. */
type PendingClose = {
  /** Fenster, die geschlossen werden, sobald bestaetigt wird. */
  ids: string[]
  /** true, wenn der Schliessbefehl ALLE Fenster betraf. */
  all: boolean
}

type WindowsContextValue = {
  windows: WindowItem[]
  activeId: string | null
  /**
   * true, sobald das Fensterlayout feststeht: der Backend-Stand ist gelesen
   * (oder nicht lesbar). Davor kann der Backend-Abgleich die Fenster noch
   * ersetzen — wer auf offene Fenster reagiert (Deep Link, #1188), wartet.
   */
  layoutRestored: boolean
  /**
   * Die Fenster, wie sie beim Start aus der lokalen Kopie kamen. Eine Adresse,
   * die genau diese zeigt, hat dieser Tab selbst geschrieben — sie ist kein
   * Link und darf nicht zurückholen, was der Backend-Stand inzwischen nicht
   * mehr enthält (#908, #1188).
   */
  localLayout: WindowItem[]
  openWindow: (id: string, title?: string, subtitle?: string, params?: WindowParams) => Promise<void>
  closeWindow: (id: string, options?: CloseOptions) => void
  closeAllWindows: (options?: CloseOptions) => void
  toggleMinimize: (id: string) => void
  minimizeAllWindows: () => WindowMinimizedState[]
  restoreWindowStates: (states: WindowMinimizedState[]) => void
  focusWindow: (id: string) => void
  updateWindowPosition: (
    id: string,
    position: WindowPosition,
      options?: {
        fromUser?: boolean
        size?: WindowSize
        snap?: 'left' | 'right' | null
        isMaximized?: boolean
      },
  ) => void
  updateWindowSubtitle: (id: string, subtitle: string | undefined) => void
  updateWindowParams: (id: string, params: WindowParams) => void
  /**
   * Fragt vor dem Schliessen dieses Fensters nach. Gibt die Abmeldefunktion
   * zurueck — im Cleanup eines `useEffect` aufrufen, sonst ueberlebt der Guard
   * sein Fenster und blockiert eine spaetere Instanz mit derselben id.
   */
  registerCloseGuard: (windowId: string, guard: CloseGuard) => () => void
  /**
   * Darf dieses Fenster JETZT schliessen? Gibt `false` zurueck, wenn ein Guard
   * zuerst gefragt werden will — die Rueckfrage laeuft dann bereits.
   *
   * Fuer die Fensterdekoration gedacht, die ihre Schliessanimation startet,
   * bevor geschlossen wird: Ohne diese Vorabfrage liefe die Animation auch bei
   * einem abgelehnten Schliessen und liesse das Fenster unsichtbar zurueck.
   */
  requestWindowClose: (windowId: string) => boolean
  /** Wie viele Fenster gerade auf eine Bestaetigung warten; 0 = kein Dialog. */
  pendingCloseCount: number
  /** true, wenn die Rueckfrage aus „alle schliessen" stammt. */
  pendingCloseIsAll: boolean
  confirmPendingClose: () => void
  cancelPendingClose: () => void
}

const WindowsContext = createContext<WindowsContextValue | null>(null)

// How long to wait after the last window change before persisting (ms)
const SAVE_DEBOUNCE_MS = 1500

export function WindowsProvider({ children }: { children: ReactNode }) {
  // Must be declared before useState so the lazy initializer can use `t`
  const { t, i18n } = useTranslation()
  const { settings } = useSettingsContext()

  // Plain helper (not useCallback) — used only inside the useState initializer below.
  // Defined here so it is in scope when the initializer runs during the first render.
  function getAppTitleForInit(appId: string): string {
    const key = `apps.${appId}`
    const translated = t(key)
    return translated !== key ? translated : appId
  }

  // ── Fast path: restore windows synchronously during the first render ───────
  // Using the useState lazy initializer (not useEffect) guarantees that windows
  // are populated BEFORE any child-component effects (e.g. useDeepLink) run,
  // which eliminates the race that caused a duplicate window to flash on reload.
  // consumeSkipRestore() is called inside the initializer so it only runs on
  // mount — not on re-renders that would prematurely consume the flag.
  const skipRestoreRef = useRef(false)
  const [windows, setWindows] = useState<WindowItem[]>(() => {
    if (consumeSkipRestore()) {
      skipRestoreRef.current = true
      return []
    }
    return loadFromLocalStorage().map((w, i) => restoreWindow(w, i, getAppTitleForInit(w.appId)))
  })
  const [localLayout] = useState(windows)
  const [activeId, setActiveId] = useState<string | null>(null)
  // windowsRef updated synchronously during render so async callbacks always see fresh state
  const windowsRef = useRef<WindowItem[]>(windows)
  windowsRef.current = windows
  const identityWindowIdsRef = useRef<Map<string, string>>(new Map())
  // Cooldown map to prevent duplicate windows from rapid double-clicks
  const openCooldownRef = useRef<Map<string, number>>(new Map())
  // Apps that want to be asked before their window closes (#850). A ref, not
  // state: registering must not re-render, and the guard is read at the moment
  // of closing, never rendered.
  const closeGuardsRef = useRef<Map<string, CloseGuard>>(new Map())
  const [pendingClose, setPendingClose] = useState<PendingClose | null>(null)
  // The backend is only written once its state has been read. Before that,
  // the windows on screen come from localStorage and may be older than what
  // another device saved — and a failed read must not turn into a write (#908).
  const backendSyncedRef = useRef(false)
  // Windows that came from localStorage. Everything else was opened in this
  // session (e.g. by a deep link) and survives the backend sync.
  const restoredIdsRef = useRef<Set<string> | null>(null)
  if (restoredIdsRef.current === null) {
    restoredIdsRef.current = new Set(windows.map((w) => w.id))
  }
  const persistToBackend = useCallback((serialized: PersistedWindowState[]) => {
    if (backendSyncedRef.current) saveToBackend(serialized)
  }, [])
  const [layoutRestored, setLayoutRestored] = useState(false)

  // ── Slow path: sync with backend (may have newer state from another device) ─
  useEffect(() => {
    let cancelled = false

    const syncWithBackend = async () => {
      if (skipRestoreRef.current) {
        backendSyncedRef.current = true
        setLayoutRestored(true)
        return
      }
      let backend: PersistedWindowState[]
      try {
        backend = await loadFromBackend()
      } catch (error) {
        logError(error as Error, {
          context: 'useWindows.syncWithBackend',
          fallback: 'Fensterlayout wird nur lokal gespeichert, bis die Seite neu geladen wird',
        })
        if (!cancelled) setLayoutRestored(true)
        return
      }
      if (cancelled) return
      backendSyncedRef.current = true
      setLayoutRestored(true)

      // The backend wins over the local copy, also when it holds no windows:
      // the local copy may be older than what another device saved (#908).
      const restoredIds = restoredIdsRef.current ?? new Set<string>()
      const current = windowsRef.current
      const restored = current.filter((w) => restoredIds.has(w.id))
      const opened = current.filter((w) => !restoredIds.has(w.id))
      if (windowSignature(backend) === windowSignature(restored)) return

      const openedKeys = new Set(opened.map(windowKey))
      const recreated = backend
        .filter((w) => !openedKeys.has(windowKey(w)))
        .map((w, i) => restoreWindow(w, i, getAppTitle(w.appId)))
      const next = [...recreated, ...opened]
      setWindows(next)
      setActiveId(next[next.length - 1]?.id ?? null)
    }

    syncWithBackend()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist windows on every change (debounced) ───────────────────────────
  useEffect(() => {
    const serialized = windows.map(serializeWindow)
    // Write to localStorage immediately (fast, synchronous)
    saveToLocalStorage(serialized)

    // Debounce the backend write to avoid hammering it during drag/resize
    const timer = setTimeout(() => {
      persistToBackend(serialized)
    }, SAVE_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [windows, persistToBackend])

  // identityWindowIdsRef synchron mit State halten — auch fuer wiederhergestellte
  // Fenster, die nie durch openWindow gelaufen sind.
  // (windowsRef.current is already updated synchronously during render above)
  useEffect(() => {
    const nextIdentityWindowIds = new Map<string, string>()
    windows.forEach((windowItem) => {
      const key = windowIdentityKey(windowItem.appId, windowItem.params)
      if (key) {
        nextIdentityWindowIds.set(key, windowItem.id)
      }
    })
    identityWindowIdsRef.current = nextIdentityWindowIds
  }, [windows])

  // Funktion zum Abrufen des übersetzten App-Titels
  const getAppTitle = useCallback((appId: string): string => {
    const translationKey = `apps.${appId}`
    const translatedTitle = t(translationKey)
    // Falls keine Übersetzung gefunden wird, den ursprünglichen Titel verwenden
    return translatedTitle !== translationKey ? translatedTitle : appId
  }, [t])

  const openWindow = useCallback(async (appId: string, title?: string, subtitle?: string, params?: WindowParams) => {
    const normalizedParams = normalizeWindowParams(params)
    // What this window shows, if it shows one particular thing (#850).
    const identityKey = windowIdentityKey(appId, normalizedParams)

    // Prevent duplicate windows from rapid double-clicks (800ms cooldown).
    // Keyed on the identity, not the app: opening two transcripts in quick
    // succession is a legitimate action, and an app-wide cooldown would drop
    // the second one without a trace.
    const cooldownKey = identityKey ?? appId
    const now = Date.now()
    const lastOpen = openCooldownRef.current.get(cooldownKey) ?? 0
    if (now - lastOpen < 800) return
    openCooldownRef.current.set(cooldownKey, now)

    // Externe URL-Apps: Zugriffscheck zuerst, dann neuen Tab öffnen — kein Fenster erstellen
    const appDef = APPS.find((a) => a.id === appId)
    if (appDef?.externalUrl) {
      try {
        const { authService } = await import('@/services/auth-service')
        const user = await authService.getCurrentUser().catch(() => null)
        if (user) {
          {
            const { userManagementService } = await import('@/services/user-management-service')
            const hasAccess = await userManagementService.canUserAccessApp(user.id, appId)
            if (!hasAccess) {
              const { default: i18n } = await import('@/i18n/config')
              toast({
                variant: 'destructive',
                title: i18n.t('apps.access.accessDeniedTitle'),
                description: i18n.t('apps.access.accessDeniedMessage'),
              })
              return
            }
          }
        }
      } catch {
        // Zugriffscheck fehlgeschlagen — fail-open, URL wird geöffnet
      }
      window.open(appDef.externalUrl, '_blank', 'noopener,noreferrer')
      return
    }

    // Dynamische URL-Apps (über Admin Panel erstellt, nicht in statischem APPS-Register):
    // 'tab' öffnet direkt einen Tab; 'window' läuft weiter wie jede App und
    // bekommt unten Fenster und Zugriffscheck (#1505).
    if (!appDef) {
      try {
        const { externalUrlAppsService } = await import('@/services/external-url-apps-service')
        const dynamicApp = await externalUrlAppsService.getApp(appId)
        if (dynamicApp?.open_mode === 'tab') {
          window.open(dynamicApp.external_url, '_blank', 'noopener,noreferrer')
          return
        }
      } catch {
        // Lookup fehlgeschlagen — als normales Fenster weiterversuchen
      }
    }

    const appTitle = title || getAppTitle(appId)

    // Synchrone Checks zuerst (blockieren nicht)
    const isBlockedInDriveOnly =
      settings.driveOnlyMode?.enabled && appId === 'file-preview'

    if (isBlockedInDriveOnly) {
      const hasIntent = appIntentService.has(appId)
      // `fileRef`: a file opened through `openFile` (#1619), e.g. a recording.
      const hasFileParams = params && (params.documentId || params.fileUrl || params.fileRef)
      const hasKnowledgeAreaParams = params && params.knowledgeArea

      if (!hasIntent && !hasFileParams && !hasKnowledgeAreaParams) {
        console.warn(`[Drive-Only] Blockiere Erstellen von ${appId} ohne Datei-Intent oder Parameter`)
        return
      }
    }

    // Identitaets-Deduplizierung: auch waehrend in-flight Opens stabil halten.
    // Zeigt ein Fenster ein bestimmtes Objekt, ist ein zweites darauf schaedlich
    // — zwei Editoren auf demselben Text ueberschreiben sich (#850).
    if (identityKey) {
      const existingWindowId = identityWindowIdsRef.current.get(identityKey)
      if (existingWindowId) {
        setWindows((prev) => {
          const target = prev.find((w) => w.id === existingWindowId)
          if (!target) return prev

          // Gesnapte Fenster sollten ihren z-index NICHT erhöhen
          if (target.snapSide === 'left' || target.snapSide === 'right') {
            return prev.map((w) =>
              w.id === target.id ? { ...w, minimized: false } : w
            )
          }

          // Normale Fenster: zIndex erhöhen
          const maxZIndex = prev.length > 0 ? Math.max(...prev.map(w => w.zIndex)) : 1000
          const isAlreadyOnTop = target.zIndex === maxZIndex

          return prev.map((w) =>
            w.id === target.id
              ? { ...w, minimized: false, ...(isAlreadyOnTop ? {} : { zIndex: maxZIndex + 1 }) }
              : w
          )
        })
        setActiveId(existingWindowId)
        return
      }
    }

    // Neues Fenster erstellen
    const instanceId = generateUUID()
    if (identityKey) {
      identityWindowIdsRef.current.set(identityKey, instanceId)
    }

    setWindows((prev) => {
      // activeId aus prev ableiten (letztes nicht-minimiertes Fenster mit hoechstem zIndex)
      const currentActiveId = prev.length > 0
        ? prev.reduce<WindowItem | null>((best, w) => {
            if (w.minimized) return best
            if (!best || w.zIndex > best.zIndex) return w
            return best
          }, null)?.id ?? null
        : null

      // Responsive open size scaled to the viewport (per-app % in shared/ui).
      // Persisted window states keep their saved size; only fresh opens use this.
      const size = calculateResponsiveWindowSize(appId)
      let position = computeInitialPosition(prev, currentActiveId, size)


      const maxZIndex = prev.length > 0 ? Math.max(...prev.map(w => w.zIndex)) : 1000
      return [
        ...prev,
        {
          id: instanceId,
          appId,
          title: appTitle,
          subtitle,
          minimized: false,
          createdAt: Date.now(),
          zIndex: maxZIndex + 1,
          position,
          isCustomPosition: false,
          size,
          isMaximized: false,
          snapSide: null,
          params: normalizedParams,
        },
      ]
    })
    setActiveId(instanceId)

    // Access-Check im Hintergrund - bei fehlenden Rechten Fenster nachtraeglich schliessen.
    // System-Apps (inkl. der Admin-"cortex"-Panel und programmatisch geoeffneter
    // Fenster wie dem herausgeloesten Collabora-Editor) sind fuer alle Nutzer
    // verfuegbar und haben teils gar keinen Backend-App-Eintrag — der Check wuerde
    // sie sonst sofort wieder schliessen. Daher fuer system: true ueberspringen.
    if (appDef?.system) return
    try {
      const { authService } = await import('@/services/auth-service')
      const user = await authService.getCurrentUser().catch(() => null)
      if (user) {
        {
          const { userManagementService } = await import('@/services/user-management-service')
          const hasAccess = await userManagementService.canUserAccessApp(user.id, appId)
          if (!hasAccess) {
            // Fenster wieder schliessen
            setWindows((prev) => prev.filter((w) => w.id !== instanceId))
            setActiveId((cur) => (cur === instanceId ? null : cur))
            const { default: i18n } = await import('@/i18n/config')
            toast({
              variant: 'destructive',
              title: i18n.t('apps.access.accessDeniedTitle'),
              description: i18n.t('apps.access.accessDeniedMessage'),
            })
          }
        }
      }
    } catch {
      // Access-Check fehlgeschlagen - Fenster bleibt offen (fail-open)
    }
  }, [getAppTitle, settings.driveOnlyMode])

  const registerCloseGuard = useCallback((windowId: string, guard: CloseGuard) => {
    closeGuardsRef.current.set(windowId, guard)
    return () => {
      // Only drop our own entry — a remount may already have registered a newer
      // guard under the same window id.
      if (closeGuardsRef.current.get(windowId) === guard) {
        closeGuardsRef.current.delete(windowId)
      }
    }
  }, [])

  /** Windows among `ids` whose app wants to be asked first. */
  const guardedAmong = useCallback((ids: string[]): string[] => {
    return guardedWindowIds(closeGuardsRef.current, ids)
  }, [])

  /** Close without asking. Every close funnels through here. */
  const closeWindowsNow = useCallback((ids: string[]) => {
    const doomed = new Set(ids)
    // Derive the next list from windowsRef (kept in sync during render) so
    // the persistence side-effect stays out of the setWindows updater —
    // updaters run twice under StrictMode and must be pure.
    const next = windowsRef.current.filter((w) => !doomed.has(w.id))
    doomed.forEach((id) => closeGuardsRef.current.delete(id))
    setWindows(next)
    setActiveId((cur) => (cur && doomed.has(cur) ? null : cur))
    // Save immediately (not debounced) to prevent ghost windows on reload.
    const serialized = next.map(serializeWindow)
    saveToLocalStorage(serialized)
    persistToBackend(serialized)
  }, [persistToBackend])

  const closeWindow = useCallback((id: string, options?: CloseOptions) => {
    if (!options?.skipGuard && guardedAmong([id]).length > 0) {
      setPendingClose({ ids: [id], all: false })
      return
    }
    closeWindowsNow([id])
  }, [guardedAmong, closeWindowsNow])

  /**
   * Vorabfrage fuer die Fensterdekoration (#850).
   *
   * `closeWindow` entscheidet dasselbe, schliesst aber auch gleich. Das X im
   * Fenster braucht die Entscheidung dagegen VOR seiner Exit-Animation: Die
   * Animation blendet das Fenster aus und schaltet Klicks ab, und ein
   * abgelehntes Schliessen liesse es genau so zurueck — unsichtbar, nicht
   * bedienbar und nicht mehr schliessbar.
   */
  const requestWindowClose = useCallback((id: string): boolean => {
    if (guardedAmong([id]).length > 0) {
      setPendingClose({ ids: [id], all: false })
      return false
    }
    return true
  }, [guardedAmong])

  const closeAllWindows = useCallback((options?: CloseOptions) => {
    const allIds = windowsRef.current.map((w) => w.id)
    // "Close all" is the easier way to lose unsaved work than closing one
    // window at a time, so it asks too — once, for however many are affected.
    if (!options?.skipGuard && guardedAmong(allIds).length > 0) {
      setPendingClose({ ids: allIds, all: true })
      return
    }
    closeWindowsNow(allIds)
  }, [guardedAmong, closeWindowsNow])

  const confirmPendingClose = useCallback(() => {
    // Read from state, not from inside a setState updater: updaters run twice
    // under StrictMode and must stay pure.
    if (!pendingClose) return
    closeWindowsNow(pendingClose.ids)
    setPendingClose(null)
  }, [pendingClose, closeWindowsNow])

  const cancelPendingClose = useCallback(() => {
    setPendingClose(null)
  }, [])

  const toggleMinimize = useCallback((id: string) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, minimized: !w.minimized } : w)))
    setActiveId((cur) => (cur === id ? null : cur))
  }, [])

  const minimizeAllWindows = useCallback((): WindowMinimizedState[] => {
    // Snapshot ueber Ref holen (stabile Referenz, kein Updater-Side-Effect)
    const currentStates: WindowMinimizedState[] = windowsRef.current.map(w => ({
      id: w.id,
      minimized: w.minimized,
    }))

    setWindows((prev) => prev.map((w) => ({ ...w, minimized: true })))
    setActiveId(null)

    return currentStates
  }, [])

  const restoreWindowStates = useCallback((states: WindowMinimizedState[]) => {
    setWindows((prev) =>
      prev.map((w) => {
        const savedState = states.find(s => s.id === w.id)
        if (savedState) {
          return { ...w, minimized: savedState.minimized }
        }
        return w
      })
    )
  }, [])

  const focusWindow = useCallback((id: string) => {
    setWindows((prev) => {
      const targetWindow = prev.find(w => w.id === id)
      if (!targetWindow) return prev

      // Gesnapte Fenster sollten ihren z-index NICHT erhöhen
      // Sie bleiben immer im Hintergrund, damit der Divider unter anderen Fenstern bleiben kann
      if (targetWindow.snapSide === 'left' || targetWindow.snapSide === 'right') {
        // Wenn bereits nicht minimiert, nichts aendern (gleiche Array-Referenz behalten)
        if (!targetWindow.minimized) return prev

        return prev.map((w) =>
          w.id === id ? { ...w, minimized: false } : w
        )
      }

      // Normale Fenster: zIndex erhöhen wie bisher
      const maxZIndex = prev.length > 0 ? Math.max(...prev.map(w => w.zIndex)) : 1000
      const isAlreadyOnTop = targetWindow.zIndex === maxZIndex

      // Wenn bereits oben und nicht minimiert, nichts aendern (gleiche Array-Referenz behalten)
      if (isAlreadyOnTop && !targetWindow.minimized) return prev

      const nextZIndex = maxZIndex + 1
      let result = prev.map((w) =>
        w.id === id
          ? { ...w, minimized: false, ...(isAlreadyOnTop ? {} : { zIndex: nextZIndex }) }
          : w
      )

      // Z-Index Normalisierung: wenn Werte zu hoch, auf 1000-basierte Sequenz zuruecksetzen
      if (nextZIndex > 5000) {
        const sorted = [...result].sort((a, b) => a.zIndex - b.zIndex)
        const zMap = new Map<string, number>()
        sorted.forEach((w, i) => zMap.set(w.id, 1000 + i))
        result = result.map(w => ({ ...w, zIndex: zMap.get(w.id)! }))
      }

      return result
    })
    setActiveId(id)
  }, [])

  const updateWindowPosition = useCallback(
    (
      id: string,
      position: WindowPosition,
      options?: {
        fromUser?: boolean
        size?: WindowSize
        snap?: 'left' | 'right' | null
        isMaximized?: boolean
      },
    ) => {
      setWindows((prev) =>
        prev.map((w) => {
          if (w.id !== id) return w

          const nextSize = options?.size
            ? {
                w: Math.max(0, options.size.w),
                h: Math.max(0, options.size.h),
              }
            : w.size

          const hasSnapProp =
            options && Object.prototype.hasOwnProperty.call(options, 'snap')
          const hasMaximizedProp =
            options && Object.prototype.hasOwnProperty.call(options, 'isMaximized')

          return {
            ...w,
            position,
            size: nextSize,
            snapSide: hasSnapProp ? options?.snap ?? null : w.snapSide,
            isMaximized: hasMaximizedProp ? Boolean(options?.isMaximized) : w.isMaximized,
            isCustomPosition: options?.fromUser ? true : w.isCustomPosition,
          }
        }),
      )
    },
    [],
  )

  const updateWindowSubtitle = useCallback((id: string, subtitle: string | undefined) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, subtitle } : w))
    )
  }, [])

  const updateWindowParams = useCallback((id: string, params: WindowParams) => {
    setWindows((prev) =>
      prev.map((w) => {
        if (w.id !== id) return w

        const normalizedPrev = normalizeWindowParams(w.params)
        const nextParams = applyWindowParamsPatch(normalizedPrev, params)

        const paramsChanged = !areWindowParamsEqual(normalizedPrev, nextParams)
        const needsNormalization = !areWindowParamsEqual(w.params, normalizedPrev)

        if (!paramsChanged && !needsNormalization) {
          return w
        }

        return {
          ...w,
          params: paramsChanged ? nextParams : normalizedPrev
        }
      })
    )
  }, [])

  // Aktualisiere alle Fenster-Titel bei Sprachänderung
  useEffect(() => {
    setWindows((prev) =>
      prev.map((w) => ({
        ...w,
        title: getAppTitle(w.appId)
      }))
    )
  }, [t, getAppTitle]) // t ändert sich bei Sprachwechsel

  // URL-App-Namen kommen erst nach dem Wiederherstellen (useUserApps registriert
  // sie per addResource, t bleibt gleich). Fenster, die noch ihren Slug als Titel
  // tragen, bekommen dann ihren Namen (#1505).
  useEffect(() => {
    const store = i18n?.store
    if (!store) return
    const retitle = () =>
      setWindows((prev) => {
        if (!prev.some((w) => w.title === w.appId && getAppTitle(w.appId) !== w.appId)) return prev
        return prev.map((w) => (w.title === w.appId ? { ...w, title: getAppTitle(w.appId) } : w))
      })
    store.on('added', retitle)
    return () => store.off('added', retitle)
  }, [i18n, getAppTitle])

  // Erstelle stabilen Hash der relevanten Fenster-Daten für Deep-Linking
  // Nur appId und params sind relevant - nicht focus, position, size, subtitle etc.
  // Subtitle is NOT part of deep link - it's set by apps based on params
  const windowsDeepLinkHash = useMemo(() => {
    return JSON.stringify(windows.map(w => ({
      appId: w.appId,
      params: w.params
    })))
  }, [windows])

  // Aktualisiere Browser-URL bei Fensteränderungen (Deep-Linking)
  // Aber nur, wenn sich die RELEVANTEN Daten ändern (appId, params)
  // Ignoriere Änderungen an focus, position, size, minimized, subtitle, createdAt
  useEffect(() => {
    // Don't update URL if we're currently processing a deep link
    if ((window as any).__deepLinkProcessing) {
      return
    }

    deepLinkService.updateBrowserUrlFromWindows(windows)
  }, [windowsDeepLinkHash, windows])

  // Aktualisiere Fenstertitel bei Sprachänderung (ohne Instanznummern)
  // Die Instanznummern wurden entfernt, da sie in der Taskbar gruppiert werden

  const value = useMemo(
    () => ({
      windows,
      activeId,
      layoutRestored,
      localLayout,
      openWindow,
      closeWindow,
      closeAllWindows,
      toggleMinimize,
      minimizeAllWindows,
      restoreWindowStates,
      focusWindow,
      updateWindowPosition,
      updateWindowSubtitle,
      updateWindowParams,
      registerCloseGuard,
      requestWindowClose,
      pendingCloseCount: pendingClose?.ids.length ?? 0,
      pendingCloseIsAll: pendingClose?.all ?? false,
      confirmPendingClose,
      cancelPendingClose,
    }),
    [windows, activeId, layoutRestored, localLayout, openWindow, closeWindow, closeAllWindows, toggleMinimize, minimizeAllWindows, restoreWindowStates, focusWindow, updateWindowPosition, updateWindowSubtitle, updateWindowParams, registerCloseGuard, requestWindowClose, pendingClose, confirmPendingClose, cancelPendingClose],
  )

  return <WindowsContext.Provider value={value}>{children}</WindowsContext.Provider>
}

export function useWindows() {
  const ctx = useContext(WindowsContext)
  if (!ctx) throw new Error('useWindows must be used within WindowsProvider')
  return ctx
}

/**
 * The window manager, or `null` outside the desktop. For code that runs in
 * both hosts — the notification center opens targets as windows in the
 * desktop and navigates under the shell-free routes (#1169).
 */
export function useWindowsOptional() {
  return useContext(WindowsContext)
}

